import type { Messages } from '../messages.js'

/**
 * Polish. `ruch` for flips, which is what a Polish board game calls a move — the thing you
 * spend, which is what the meter counts.
 *
 * Four plural forms, and the trap is that `other` is not a spare: Polish uses it for fractions,
 * so `2,5 słowa` needs the same form as `2 słowa` rather than the genitive `słów`.
 */
export const pl: Messages = {
  tag: 'pl',

  readingDictionary: 'Czytam słownik…',
  noWordList: 'Brak listy słów dla „{language}”. Zbuduj ją:  pnpm dictionary build',
  emptyWordList: 'Lista słów dla „{language}” jest pusta.',

  flips: 'ruchy',
  score: 'wynik',
  words: 'słowa',
  round: 'runda',
  ticksLeftLabel: 'Czas do końca rundy',
  typeAWord: 'wpisz słowo',
  tapPrompt: 'dotknij liter, aby wybrać lub cofnąć, potem {action}',

  boardOfTiles: 'Plansza z {n} płytek',
  faceDown: 'zakryta',
  wildCard: 'joker',
  wildKey: 'dowolna litera',
  letterReplaced: '{from} zmieniło się w {to}',
  letterSwap: 'ZAMIANA LITER!',
  spentTile: 'zużyta płytka',
  hiddenWhilePaused: 'ukryte na czas pauzy',
  letterInWord: '{letter}, litera {position} w słowie',

  completeWord: 'Zatwierdź słowo',

  completeShort: 'Zatwierdź',
  reset: 'Wyczyść',
  pause: 'Pauza',
  resume: 'Wznów',
  newGame: 'Nowa gra',
  paused: 'Pauza',
  outOfFlips: 'Koniec ruchów',
  finalResult: '{score} za {words} w {rounds}',
  playAgain: 'Zagraj jeszcze raz',
  share: 'Udostępnij',
  shareCopied: 'Skopiowano.',
  shareSelect: 'Skopiuj to:',

  lettersSelect: 'litery wybierają',
  keysWild: 'brany, gdy wpiszesz literę, której nie ma na planszy',
  clearsEvery: 'usuwa wszystkie wybrane {letter}',
  undoLastLetter: 'cofnij ostatnią literę',
  noWordsYet: 'Jeszcze żadnych słów.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'już znalezione',
  reasonTooShort: 'za krótkie',
  reasonNotAWord: 'to nie jest słowo',
  reasonAllFound: 'masz już wszystkie',
  noSuchLetterUp: 'brak odkrytego {letter}',
  nothingUp: 'nic nie odkryto',
  letterHidden: 'litera się ukryła',
  shuffled: 'przetasowano',
  shuffledAndBilled: 'przetasowano, policzono {flips} niewykorzystanych',
  tooFewLetters: 'Zostało za mało liter',

  gameLanguage: 'język',
  interfaceLanguage: 'interfejs',
  dictionarySize: '{common} codziennych z {full} słów',
  filterLanguages: 'Szukaj języka',
  noMatches: 'Brak wyników',

  nerdMode: 'tryb dla zapaleńców',
  rules: 'Zasady',
  difficulty: 'poziom',
  difficultyNames: { easy: 'łatwy', medium: 'średni', hard: 'trudny', insane: 'szalony' },
  pausedNoBoard: 'Ta gra nie trafi do rankingu.',
  about: 'O grze',
  themeLabel: 'motyw',
  themeNames: { traditional: 'tradycyjny', light: 'jasny', contrast: 'wysoki kontrast' },
  tiles: 'płytki (N)',
  secondsPerTick: 'sekundy / takt',
  holdTicks: 'takty postoju',
  minWord: 'najkrótsze słowo',
  startingFlips: 'ruchy na start',
  wildChance: 'szansa na jokera',
  replaceChance: 'szansa na zamianę liter',
  hideChance: 'szansa na ukrycie litery',
  wordCompleteMode: 'po ułożeniu słowa',
  wordCompleteNames: { shuffle: 'tasowanie', spend: 'zużycie', keep: 'zostawienie' },
  flipEconomy: 'zwrot ruchów',
  flipEconomyNames: {
    none: 'brak',
    perLetter: 'za literę',
    fibonacci: 'fibonacci',
    overMinimum: 'ponad minimum',
  },
  repeatedLetterKey: 'klawisz powtórzonej litery',
  keySchemeNames: { cycle: 'cykl', advance: 'krok' },
  keySchemeHelp: {
    cycle:
      'A bierze kolejne wolne A, a gdy wszystkie są już w słowie, usuwa je. ' +
      'Shift+A też je usuwa.',
    advance: 'A bierze kolejne wolne A. Shift+A usuwa wszystkie A ze słowa.',
  },

  whatThatMeans: 'Co to znaczy',
  factRound: 'runda',
  factWholeBoardUp: 'cała plansza odkryta przez',
  factRoundCosts: 'runda kosztuje',
  factFlipsBuy: 'ruchy na start kupują',
  factThisBoard: 'ta plansza',
  factBoardHadToAdmit: 'plansza musiała dopuścić',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, najdłuższe {longest}',
  wordsIncludingOneOf: '{words}, w tym jedno z {ceiling}',
  scorelessRounds: '{rounds} bez punktów',

  whatAWordPays: 'Ile daje słowo',
  columnLetters: 'litery',
  columnCost: 'koszt',
  columnPoints: 'punkty',
  columnFlips: 'ruchy',
  columnNet: 'bilans',

  canonicalRules: 'Standardowe zasady poziomu {difficulty}.',
  customRules: 'Zmienione względem ustawień. Wyniki na własnych zasadach nie trafiają do rankingu.',
  applyAndStart: 'Zastosuj i zacznij nową grę',
  changesNextGame: 'Zmiany zadziałają od następnej gry.',
  presets: 'Ustawienia:',

  start: 'Start',
  restart: 'Od nowa',
  quit: 'Zakończ',
  quitTitle: 'Zakończyć tę grę?',
  restartTitle: 'Zacząć tę grę od nowa?',
  restartConfirm: 'Od nowa',
  quitConfirm: 'Zakończ',
  keepPlaying: 'Graj dalej',
  personalBest: 'Twoje najlepsze gry',
  newPersonalBest: 'Nowy rekord.',
  columnRank: '#',
  notRanked: 'Własne zasady, więc ta gra nie trafia do rankingu.',
  notRankedPaused: 'Zegar się zatrzymał, więc ta gra nie trafia do rankingu.',
  rankOfTotal: '{rank} z {total}',

  howToPlay: 'Jak grać',

  backToGame: 'Wróć do gry',
  welcomeTitle: 'Witaj w Blinkered',
  tutorialSkip: 'Pomiń',
  tutorialNext: 'Dalej',
  tutorialBack: 'Wstecz',
  tutorialStart: 'Zacznij grę',
  tutorialHideAgain: 'Nie pokazuj tego więcej',
  tutorialProgress: '{n} z {total}',
  tutorialSkipTitle: 'Pominąć wprowadzenie?',
  tutGoalTitle: 'Blinkered to wyścig',
  tutGoalBody:
    'Zaczynasz grę ze stałą liczbą ruchów, a każda odkryta płytka kosztuje jeden ruch. Gdy ruchy się skończą, gra dobiega końca. Słowa dają ruchy, a im dłuższe słowo, tym więcej ruchów zdobywasz.',
  tutSpentBody:
    'Litery użyte w słowie pozostają zakryte do następnej rundy. Tylko zmiana litery zmienia to, jakie litery są na planszy.',
  tutChangeTitle: 'Litery mogą się zmieniać',
  tutSameLetters:
    'Słowo, którego nie udało się ułożyć na czas? Te same litery wrócą w następnej rundzie, przetasowane na nowe miejsca.',
  tutPickLetters: 'Dotykaj liter po kolei, aby ułożyć słowo.',
  tutMoreTurn: 'Płytki odkrywają się, kiedy myślisz, więc lepsza litera może dopiero nadejść.',
  tutTapBack:
    'Wzięłaś lub wziąłeś nie tę literę? Dotknij jej jeszcze raz, aby ją oddać. Dowolną, nie tylko ostatnią.',
  tutComplete: 'Naciśnij Zatwierdź, gdy słowo jest gotowe.',
  htBoardTitle: 'Plansza',
  htBoardBody:
    'Płytki odkrywają się pojedynczo, w losowej kolejności. Z odkrytych układa się słowa.',
  htWordsTitle: 'Słowa',
  htWordsBody: 'Ułóż słowo z odkrytych płytek, wpisując lub klikając litery po kolei.',
  htFlipsTitle: 'Ruchy',
  htFlipsBody:
    'Każda odkryta płytka kosztuje ruch. Ułożone słowo zwraca ruchy, a dłuższe słowa zwracają więcej. Gdy ruchy się skończą, gra dobiega końca.',
  htRoundTitle: 'Runda',
  htRoundBody:
    'Gdy odkryje się ostatnia płytka rundy, cała plansza zatrzymuje się na chwilę. Potem płytki zostają zakryte i przetasowane, i zaczyna się nowa runda.',
  htLanguagesTitle: 'Języki',
  htLanguagesBody:
    'Jest ich {n}. Każdą planszę da się rozwiązać słowami, których ludzie naprawdę używają. Rzadkie słowo też punktuje, jeśli słownik je zna.',
  htKeysTitle: 'Klawiatura',
  htWildTitle: 'Jokery',
  htWildBody:
    'Czasem zamiast litery pojawia się joker. Joker liczy się jako każda litera, która tworzy poprawne słowo. Słowo już ułożone się nie liczy.',
  htHideTitle: 'Ukrywające się litery',
  htHideBody:
    'Na trudniejszych poziomach odkryta litera może sama się odwrócić i wrócić później w tej samej rundzie. Zegar dostaje za to dodatkową kreskę, na czerwono. Już wybrane litery nie odwracają się.',
  tutHideBody:
    'Na trudniejszych poziomach odkryta litera może sama się odwrócić i wrócić później w tej samej rundzie. Już wybrane litery nie odwracają się.',
  htSwapTitle: 'Zmiana liter',
  htSwapBody:
    'Czasem między rundami jedna litera zostaje zastąpiona inną. Zobaczysz, która zniknęła i która się pojawiła.',
  htLevelsTitle: 'Poziomy',
  htLevelEasy:
    'Te same dwanaście liter przez całą grę, więc można je zapamiętać i nosić listę słów w głowie. Płytki odkrywają się powoli, a cała plansza jest widoczna dość długo, by dokończyć wybór.',
  htLevelMedium:
    'Co jakiś czas litera się zmienia, więc trudniej zapamiętać słowa odłożone na później. Mniej czasu na patrzenie i mniej na myślenie.',
  htLevelHard:
    'Słowa trzyliterowe przestają się liczyć, a litera zmienia się mniej więcej co drugą rundę. Plansza ledwie się pokazuje, a już się tasuje.',
  htLevelInsane:
    'Wszystko naraz i na pełnej prędkości. Plansza tasuje się niemal zaraz po ostatnim ruchu.',
  htTouchTitle: 'Ekran dotykowy',
  htTouchBody:
    'Dotknij odkrytej płytki, aby wziąć jej literę. Dotknij wziętej litery, aby ją oddać. Zatwierdź i Wyczyść są pod planszą.',

  accountTitle: 'Twoje konto',
  menuProfile: 'Mój profil',
  menuGames: 'Moje gry',
  menuPublicPage: 'Moja strona publiczna',
  menuSignedInAs: 'Zalogowano jako',
  signIn: 'Zaloguj się',
  signOut: 'Wyloguj się',
  signInTitle: 'Zaloguj się lub załóż konto',
  signInLead: 'Konto przechowuje twoje gry, na każdym urządzeniu.',
  signInKeepGame: 'Zaloguj się, aby zachować tę grę, i każdą następną.',
  continueWithApple: 'Kontynuuj z Apple',
  continueWithGoogle: 'Kontynuuj z Google',
  signInOr: 'albo',
  emailLabel: 'E-mail',
  codeLabel: 'Kod',
  emailMeACode: 'Wyślij mi kod',
  working: 'Chwileczkę…',
  sendNewCode: 'Wyślij nowy kod',
  codeSent: 'Sprawdź pocztę. Kod jest ważny 10 minut.',
  notNow: 'Nie teraz',
  badEmail: 'To nie wygląda na adres e-mail.',
  badCode: 'Ten kod nie zadziałał. Kody wygasają po 10 minutach i działają raz.',
  serverBusy: 'Nie udało się połączyć z serwerem. Spróbuj za chwilę.',
  serverDown: 'Nie udało się połączyć z serwerem.',
  offline: 'Offline',
  leaderboardTitle: 'Tabela wyników',
  leaderboardEmpty: 'Nie ma jeszcze gier w tej tabeli. Bądź pierwszy.',
  leaderboardWouldBe: 'Gdzie znalazłaby się ta gra',
  leaderboardThisGame: 'Ta gra',
  leaderboardRanksHere: 'Gdzie znajduje się ta gra',
  leaderboardOutside: 'Poza pierwszą piątką.',
  tutAccountTitle: 'Zachowaj swoje wyniki',
  tutAccountBody: 'Konto zapisuje twoje gry, umieszcza wyniki w tabeli i przechowuje twój profil.',
  ssoCancelled: 'Logowanie anulowane.',
  ssoExpired: 'To trwało za długo. Zaloguj się jeszcze raz.',
  ssoNoUsername: 'Nie udało się dokończyć zakładania konta. Spróbuj za chwilę.',
  ssoFailed: 'Nie zadziałało. Spróbuj jeszcze raz albo użyj e-maila.',
  tabProfile: 'Profil',
  countryAny: 'Wolę nie mówić',
  findCountry: 'Szukaj krajów',
  usernameLabel: 'Nazwa użytkownika',
  bioLabel: 'O mnie',
  countryLabel: 'Kraj',
  bioHint: 'Bez linków. {left}/{max}',
  save: 'Zapisz',
  saving: 'Zapisywanie…',
  saved: 'Zapisano.',
  nameTaken: 'Ktoś już ma tę nazwę.',
  nameTooShort: 'Trochę dłuższa, proszę.',
  nameTooLong: 'To za długie.',
  nameBadCharacters: 'Tylko litery, cyfry, myślniki i podkreślenia.',
  nameBadEdges: 'Musi zaczynać się i kończyć literą albo cyfrą.',
  nameMixedScripts: 'Wybierz jeden alfabet i przy nim zostań.',
  nameReserved: 'To wygląda na nazwę, którą sami przydzielamy. Wybierz inną.',
  nameUnusable: 'Tej nazwy nie można użyć.',
  bioTooLong: 'Opis mieści najwyżej {max} znaków.',
  bioHasLink: 'Linki nie są dozwolone w opisie.',
  bioHasControl: 'Zawiera znaki, których opis nie może mieć.',
  bioUnusable: 'Tego opisu nie można użyć.',
  gamesHeading: 'Gry',
  gamesEmpty: 'Jeszcze nic tu nie ma. Gry rozegrane po zalogowaniu pojawią się tutaj.',
  gamesEmptyShort: 'Jeszcze nic tu nie ma.',
  gamesLoading: 'Wczytywanie twoich gier…',
  theirGamesLoading: 'Wczytywanie jego gier…',
  profileLoading: 'Wczytywanie profilu…',
  playerNotFound: 'Nie ma tu nikogo o tej nazwie.',
  columnWhen: 'Kiedy',
  columnRounds: 'rundy',
  columnGame: 'Gra',
  gameLoading: 'Wczytywanie gry…',
  gameUnreadable: 'Nie udało się wczytać tej gry.',
  gameNotRanked: 'Rozegrana na zmienionych zasadach, więc nie jest klasyfikowana.',
  detailNotKept: 'Słowa i plansze tej gry nie są już przechowywane.',
  openingBoard: 'Plansza początkowa',
  roundByRound: 'Runda po rundzie',
  roundNothing: 'nic nie znaleziono',
  changeReplaced: '{letter} wymieniona w polu {slot}',
  changeWildOn: '{letter} joker w polu {slot}',
  changeWildOff: '{letter} joker zniknął z pola {slot}',
  keepThisGame: 'Zachowaj tę grę',
  savedToYourGames: 'Zapisano w twoich grach.',

  reportThis: 'Zgłoś',
  reportHeading: 'Zgłoś to',
  reportTheName: 'Nazwa',
  reportTheBio: 'Opis',
  reportTheScore: 'Wynik',
  reportWhy: 'Co jest z tym nie tak? Opcjonalne.',
  reportSend: 'Wyślij zgłoszenie',
  reportSent: 'Dziękujemy. Ktoś to sprawdzi.',
  reportSignIn: 'Zaloguj się, żeby coś zgłosić.',
  reportGone: 'Tego już nie ma.',
  reportCancel: 'Anuluj',

  deleteAccount: 'Usuń moje konto',
  deleteAccountWhat:
    'To na zawsze usuwa twój profil i każdą zachowaną grę. Nie da się tego cofnąć.',
  deleteAccountAsk: 'Wyślij mi kod',
  deleteConfirm: 'Usuń na zawsze',
  deleteGone: 'Twojego konta już nie ma. Dzięki za grę.',
  deleteNoAddress:
    'Na tym koncie nie ma potwierdzonego adresu, więc nie można sprawdzić usunięcia. Napisz do nas, a zrobimy to ręcznie.',

  rulesetFacts: 'Słowa od {letters} · nowa płytka co {seconds}',
  plurals: {
    words: { one: '{n} słowo', few: '{n} słowa', many: '{n} słów', other: '{n} słowa' },
    rounds: { one: '{n} runda', few: '{n} rundy', many: '{n} rund', other: '{n} rundy' },
    flips: { one: '{n} ruch', few: '{n} ruchy', many: '{n} ruchów', other: '{n} ruchu' },
    ticks: { one: '{n} takt', few: '{n} takty', many: '{n} taktów', other: '{n} taktu' },
    points: { one: '{n} punkt', few: '{n} punkty', many: '{n} punktów', other: '{n} punktu' },
    minLetters: { one: '{n} litery', few: '{n} liter', many: '{n} liter', other: '{n} litery' },
    seconds: { one: '{n} sekundy', few: '{n} sekundy', many: '{n} sekundy', other: '{n} sekundy' },
  },
}
